import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from './authHooks'
import { useAuth0 } from "@auth0/auth0-react"
import { isAuthVerificationFailed } from '../../../lib/authVerificationState'
import { ProductRecoveryScreen } from '../errors/ProductRouteStates'
import { resolveSessionExpiryDestination } from '../../../lib/sessionExpiry'
import {
    BETA_LANDING_PATH,
    parseCandidateSafeReturn,
    storeCandidateSafeReturn,
    storeSafeReturn,
} from '../../../lib/safeReturn'
import {
    isChooseRoleSuppressed,
    resolveSignedInDestination,
} from '../family/familyAccountHome'
import {
    useProductDeploymentProfile,
    useProductNavigate,
} from '../navigation/ProductNavigation'
import { resolveCandidateProductAccess } from './productAccessPolicy'
import { recordOperationalEvent } from '../../../analytics/operationalRuntime'
import {
    recordConsentedProductLoginFailed,
    recordConsentedProductLoginStarted,
    recordProductLoginCompleted,
} from '../../../analytics/productAnalyticsRuntime'

import { SESSION_CHECK_TIMEOUT_MS } from '../../../pages/loginConstants';

const GOOGLE_LOGIN_ATTEMPT_KEY = 'pp_product_google_login_attempt_v1';

function beginGoogleLoginAttempt() {
    const attempt = { loginAttemptId: crypto.randomUUID(), startedAt: Date.now() };
    try { sessionStorage.setItem(GOOGLE_LOGIN_ATTEMPT_KEY, JSON.stringify(attempt)); } catch { /* correlation is best effort */ }
    return attempt;
}

function readGoogleLoginAttempt() {
    try {
        const value = JSON.parse(sessionStorage.getItem(GOOGLE_LOGIN_ATTEMPT_KEY) ?? 'null');
        return typeof value?.loginAttemptId === 'string' && typeof value?.startedAt === 'number' ? value : null;
    } catch { return null; }
}

function clearGoogleLoginAttempt() {
    try { sessionStorage.removeItem(GOOGLE_LOGIN_ATTEMPT_KEY); } catch { /* unavailable storage */ }
}

function recordLoginStarted(loginAttemptId, methodCategory) {
    void recordOperationalEvent({ event: 'login_started', loginAttemptId, methodCategory });
    recordConsentedProductLoginStarted(loginAttemptId, methodCategory);
}

function recordLoginFailed(loginAttemptId, methodCategory, reasonCategory) {
    void recordOperationalEvent({ event: 'login_failed', loginAttemptId, methodCategory, reasonCategory });
    recordConsentedProductLoginFailed(loginAttemptId, methodCategory, reasonCategory);
}

// plan-single-verifier-owner.md §2.2 — thin wrapper reusing the existing
// resolveSignedInDestination (AGENTS.md § Reuse before invention) so the
// interactive-login trigger and the `user` effect below compute the exact
// same destination from the exact same inputs.
function resolveLoginDestination(user, requestedReturnTo) {
    return resolveSignedInDestination(user, requestedReturnTo);
}

function resolveCandidateLoginOutcome(user, requestedReturnTo) {
    const safeReturnTo = parseCandidateSafeReturn(requestedReturnTo);
    const resolution = resolveCandidateProductAccess({
        user,
        pathname: safeReturnTo ?? '/sat',
        chooseRoleSuppressed: isChooseRoleSuppressed(user),
    });
    if (resolution.kind === 'recover') {
        return {
            kind: 'recover',
            recoveryKind: resolution.reason === 'no-usable-area'
                ? 'product-access-empty'
                : 'product-access-invalid',
        };
    }
    if (resolution.kind === 'redirect') {
        return { kind: 'navigate', destination: resolution.destination };
    }
    if (safeReturnTo) return { kind: 'navigate', destination: safeReturnTo };
    return { kind: 'navigate', destination: '/practice' };
}

function resolveLoginOutcome(user, requestedReturnTo, deploymentProfile) {
    if (deploymentProfile === 'www-candidate') {
        return resolveCandidateLoginOutcome(user, requestedReturnTo);
    }
    return { kind: 'navigate', destination: resolveLoginDestination(user, requestedReturnTo) };
}

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [sessionCheckFailed, setSessionCheckFailed] = useState(false);

    const { user, loginWithEmail, handleExternalLogin, verifying } = useAuth();
    const { loginWithRedirect, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
    const navigate = useProductNavigate();
    const deploymentProfile = useProductDeploymentProfile();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const sessionExpired = query.get('reason') === 'session-expired';
    const requestedReturnTo = location.state?.returnTo ?? query.get('next');
    const safeReturnTo = deploymentProfile === 'www-candidate'
        ? parseCandidateSafeReturn(requestedReturnTo)
        : resolveSessionExpiryDestination(requestedReturnTo);
    const hasTokenOnlySession = user && !user.email;
    const verificationFailed = hasTokenOnlySession && isAuthVerificationFailed();
    const isSessionChecking = !sessionCheckFailed && (
        isLoading
        || (isAuthenticated && !user)
        || (hasTokenOnlySession && verifying && !verificationFailed)
    );
    const expiryHeading = useRef(null);
    const loginHeading = useRef(null);
    const verifiedLoginOutcome = user?.email
        ? resolveLoginOutcome(user, requestedReturnTo, deploymentProfile)
        : null;
    const verifiedDestination = verifiedLoginOutcome?.kind === 'navigate'
        ? verifiedLoginOutcome.destination
        : null;

    useEffect(() => {
        if (hasTokenOnlySession) return;
        if (sessionExpired) {
            expiryHeading.current?.focus();
        } else {
            loginHeading.current?.focus();
        }
    }, [sessionExpired, hasTokenOnlySession]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (!params.get('error')) return;
        const attempt = readGoogleLoginAttempt();
        if (attempt) {
            const reasonCategory = params.get('error') === 'access_denied' ? 'provider_cancelled' : 'provider_unavailable';
            recordLoginFailed(attempt.loginAttemptId, 'google', reasonCategory);
            clearGoogleLoginAttempt();
        }
        navigate('/auth/callback-error', {
            replace: true,
            state: { reason: params.get('error_description') || 'Sign-in provider error' },
        });
    }, [location.search, navigate]);

    // If already verified, redirect to the beta app (or stored return path).
    // A token-only user is still waiting on /api/users/me; do not navigate yet,
    // or inactive users briefly land in the app before the access gate redirects.
    useEffect(() => {
        if (verifiedDestination && !sessionCheckFailed) {
            navigate(verifiedDestination, { replace: true });
        }
    }, [navigate, sessionCheckFailed, verifiedDestination]);

    useEffect(() => {
        if (!isSessionChecking) return undefined;
        const timeout = window.setTimeout(
            () => setSessionCheckFailed(true),
            SESSION_CHECK_TIMEOUT_MS,
        );
        return () => window.clearTimeout(timeout);
    }, [isSessionChecking]);

    // Sync Auth0 State
    useEffect(() => {
        let active = true;
        const syncAuth = async () => {
            if (isAuthenticated && !user && !isLoading && !sessionCheckFailed) {
                try {
                    const token = await getAccessTokenSilently();
                    if (!active) return;
                    await handleExternalLogin(token, safeReturnTo);
                    const attempt = readGoogleLoginAttempt();
                    if (attempt) {
                        clearGoogleLoginAttempt();
                        void recordProductLoginCompleted(
                            attempt.loginAttemptId, 'google', Math.max(0, Date.now() - attempt.startedAt),
                        ).catch(() => undefined);
                    }
                    // Navigation handled by the user effect above
                } catch (error) {
                    if (!active) return;
                    const attempt = readGoogleLoginAttempt();
                    const accessPending = error?.response?.status === 403
                        && error?.response?.data?.detail?.code === 'access_pending';
                    if (attempt) {
                        if (!accessPending) {
                            const reasonCategory = error?.response?.status === 401 ? 'unauthorized'
                                : error?.response?.status === 403 ? 'forbidden'
                                    : window.navigator.onLine === false ? 'network' : 'provider_unavailable';
                            recordLoginFailed(attempt.loginAttemptId, 'google', reasonCategory);
                        }
                        clearGoogleLoginAttempt();
                    }
                    console.error('Failed to sync Auth0 session:', error);
                    if (!accessPending) setSessionCheckFailed(true);
                }
            }
        };
        syncAuth();
        return () => {
            active = false;
        };
    }, [isAuthenticated, user, isLoading, getAccessTokenSilently, handleExternalLogin, safeReturnTo, sessionCheckFailed]);

    // (a5 at the page layer) Re-entrancy guard — closes R6 for the submit
    // path. `disabled={isLoggingIn}` protects the button, but state is async
    // and cannot guard two synchronous `fireEvent.submit` calls in the same
    // tick (U17).
    const loginSubmitInFlightRef = useRef(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (loginSubmitInFlightRef.current) return;
        loginSubmitInFlightRef.current = true;
        try {
            const loginAttemptId = crypto.randomUUID();
            const loginStartedAt = performance.now();
            recordLoginStarted(loginAttemptId, 'password');
            setLoginError('');
            setIsLoggingIn(true);
            const result = await loginWithEmail(email, password, safeReturnTo);
            if (result.success) {
                void recordProductLoginCompleted(loginAttemptId, 'password', performance.now() - loginStartedAt).catch(() => undefined);
                // (b) Navigate from the resolved outcome immediately — do not
                // depend solely on the later `user` effect (plan §2.2). The
                // `user` effect stays: it is still the navigation owner for
                // the Auth0 path and for a boot-verified user landing on
                // /login. Both call the same helper with the same inputs and
                // both use replace:true, so a second run is a no-op.
                const outcome = resolveLoginOutcome(result.user, requestedReturnTo, deploymentProfile);
                if (outcome.kind === 'navigate') {
                    navigate(outcome.destination, { replace: true });
                }
            } else if (result.recoveryPath) {
                recordLoginFailed(loginAttemptId, 'password', 'network');
                navigate(result.recoveryPath, { replace: true });
            } else if (!result.accessPending) {
                recordLoginFailed(loginAttemptId, 'password', 'credentials_rejected');
                setLoginError(result.error);
            }
        } finally {
            setIsLoggingIn(false);
            loginSubmitInFlightRef.current = false;
        }
    };

    if (sessionCheckFailed) {
        return <ProductRecoveryScreen kind="session-check-failure" />;
    }

    if (isSessionChecking) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center p-4 text-center" role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-sm font-medium text-slate-600">Checking your sign-in...</p>
            </div>
        );
    }

    if (hasTokenOnlySession) {
        return <ProductRecoveryScreen kind="api-unavailable" />;
    }

    if (verifiedLoginOutcome?.kind === 'recover') {
        return <ProductRecoveryScreen kind={verifiedLoginOutcome.recoveryKind} />;
    }

    if (user?.email) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center p-4 text-center" role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-sm font-medium text-slate-600">Loading your practice...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center p-4">
            <div className="w-full max-w-sm bg-white rounded-xl shadow-xl p-8 border border-slate-200">
                <div className="text-center mb-8">
                    {sessionExpired && (
                        <div role="alert" className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-amber-900">
                            <h1 ref={expiryHeading} tabIndex={-1} className="text-xl font-bold">Your session expired</h1>
                            <p className="mt-1 text-sm">Sign in again to continue safely.</p>
                        </div>
                    )}
                    {sessionExpired
                        ? <p className="text-3xl font-bold text-slate-800 mb-2">Pinpoint</p>
                        : <h1 ref={loginHeading} tabIndex={-1} className="text-3xl font-bold text-slate-800 mb-2">Pinpoint</h1>}
                    <p className="text-slate-500">Sign in to continue your progress</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                        <label htmlFor="login-email" className="block text-sm font-medium text-slate-700 mb-1">Email or username</label>
                        <input
                            id="login-email"
                            type="text"
                            autoComplete="username"
                            required
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="name@example.com or username"
                        />
                    </div>
                    <div>
                        <label htmlFor="login-password" className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                        <input
                            id="login-password"
                            type="password"
                            required
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                        />
                    </div>

                    {loginError && (
                        <div role="alert" className="text-red-500 text-sm bg-red-50 p-2 rounded border border-red-100 animate-in fade-in">
                            {loginError}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isLoggingIn}
                        className="w-full bg-blue-600 text-white font-semibold py-2.5 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-sm"
                    >
                        {isLoggingIn ? 'Signing In...' : 'Sign In'}
                    </button>

                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-slate-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-slate-500">Or continue with</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => {
                            setLoginError('');
                            const attempt = beginGoogleLoginAttempt();
                            recordLoginStarted(attempt.loginAttemptId, 'google');
                            const callbackReturnTo = deploymentProfile === 'www-candidate'
                                ? storeCandidateSafeReturn(safeReturnTo)
                                : (storeSafeReturn(safeReturnTo) ?? BETA_LANDING_PATH);
                            const recordProviderFailure = () => {
                                recordLoginFailed(attempt.loginAttemptId, 'google', 'provider_unavailable');
                                clearGoogleLoginAttempt();
                                // Never fail silently. Without this the button records
                                // telemetry and does nothing visible, so the student sees
                                // a dead control -- the same silent-failure shape as the
                                // dropped callback this file's fix addresses.
                                setLoginError('Google sign-in could not be started. Please try again.');
                            };
                            try {
                                const appState = callbackReturnTo ? { returnTo: callbackReturnTo } : {};
                                const redirect = loginWithRedirect({ appState, authorizationParams: { connection: 'google-oauth2' } });
                                if (redirect && typeof redirect.catch === 'function') void redirect.catch(recordProviderFailure);
                            } catch {
                                recordProviderFailure();
                            }
                        }}
                        className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-slate-300 rounded-lg shadow-sm bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                        <svg className="h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg>
                        Continue with Google
                    </button>
                </form>
            </div>
        </div>
    )
}
